import React, { useState } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { projectsData } from '../mockData';

const Projects = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState({});

  const nextImage = (projectId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) + 1) % imagesLength
    }));
  };

  const prevImage = (projectId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  return (
    <section id="projects" className="py-24 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with View All Button */}
        <div className="text-center mb-16">
          <Button
            variant="outline"
            size="lg"
            className="mb-6 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-800"
            onClick={() => window.open('#', '_blank')}
          >
            <Eye className="w-4 h-4 mr-2" />
            View All Client Projects
          </Button>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Client Projects
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Real businesses, real results – see our live websites in action
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {projectsData.map((project, index) => {
            const currentIndex = currentImageIndex[project.id] || 0;
            return (
              <Card
                key={project.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white dark:bg-gray-800 border-2"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Project Image Gallery */}
                <div className="relative h-64 overflow-hidden bg-gray-200 dark:bg-gray-700">
                  <img
                    src={project.images[currentIndex]}
                    alt={`${project.title} - Image ${currentIndex + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Image Navigation */}
                  {project.images.length > 1 && (
                    <>
                      <button
                        onClick={() => prevImage(project.id, project.images.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => nextImage(project.id, project.images.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      
                      {/* Image Indicators */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                        {project.images.map((_, idx) => (
                          <div
                            key={idx}
                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                              idx === currentIndex ? 'bg-white w-6' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900 dark:text-white">
                    {project.title}
                  </CardTitle>
                  <Badge className="w-fit bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-0">
                    {project.businessType}
                  </Badge>
                </CardHeader>

                <CardContent>
                  <CardDescription className="text-gray-600 dark:text-gray-400 mb-6 text-base">
                    {project.description}
                  </CardDescription>

                  {/* Live Demo Button */}
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => window.open(project.liveUrl, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Live Website
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-12 border-2 border-blue-200 dark:border-blue-800">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Ready to Launch Your Website?
          </h3>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
            We handle everything from design to deployment. No technical knowledge needed – just tell us your business needs.
          </p>
          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Start Your Project Today
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Projects;
